import SEO from '../shared/SEO';
import { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfigContext } from '../../providers/ConfigContext';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const getPaymentIcon = (method) => {
  const icons = {
    efectivo: 'fa-money-bill-wave',
    transferencia: 'fa-building-columns',
    mercadopago: 'fa-credit-card',
    mercadopago_debito: 'fa-credit-card',
    mercadopago_credito: 'fa-credit-card',
  };
  return icons[method] || 'fa-credit-card';
};

const OrderSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { config } = useContext(ConfigContext);

  const {
    orderNumber = 'N/A',
    items = [],
    subtotal = 0,
    total = 0,
    shippingOption = {},
    buyerInfo = {},
    paymentMethodInfo = {},
    notes = ''
  } = location.state || {};

  const isPickup = ['retiro_local', 'retiro_blandengues', 'retiro_ferrer_serra'].includes(shippingOption.type);

  return (
    <>
    <SEO title="Pedido Confirmado" />
    <div style={{
      minHeight: '60vh',
      background: 'var(--bg-secondary, #f8f6f3)',
      padding: '48px 16px 64px'
    }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>

        {/* Header card */}
        <div style={{
          background: 'var(--bg-primary, #fff)',
          border: '1px solid var(--border-light, #e8e0d8)',
          padding: '40px 32px',
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'var(--success-soft, #edfaf3)',
            border: '1.5px solid var(--success, #2ecc71)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <i className="fas fa-check" style={{ fontSize: '1.4rem', color: 'var(--success, #2ecc71)' }}></i>
          </div>

          <p style={{
            fontFamily: 'var(--font-body, Inter, sans-serif)',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--text-muted, #999)',
            marginBottom: '8px'
          }}>
            ¡Gracias por tu compra!
          </p>

          <h1 style={{
            fontFamily: 'var(--font-heading, "Cormorant Garamond", serif)',
            fontSize: '2rem',
            fontWeight: 400,
            letterSpacing: '0.04em',
            color: 'var(--text-primary, #1a1a1a)',
            marginBottom: '12px'
          }}>
            Pedido Confirmado
          </h1>

          <p style={{
            fontFamily: 'var(--font-body, Inter, sans-serif)',
            fontSize: '0.88rem',
            color: 'var(--text-muted, #777)',
            fontWeight: 300,
            lineHeight: 1.6,
            marginBottom: '20px'
          }}>
            Tu pedido fue registrado exitosamente. Nos contactaremos con vos a la brevedad.
          </p>

          <div style={{
            display: 'inline-block',
            background: 'var(--bg-secondary, #f8f6f3)',
            border: '1px solid var(--border-light, #e8e0d8)',
            padding: '10px 24px'
          }}>
            <span style={{
              fontFamily: 'var(--font-body, Inter, sans-serif)',
              fontSize: '0.72rem',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--text-muted, #999)',
              marginRight: 8
            }}>N° de pedido</span>
            <span style={{
              fontFamily: 'var(--font-heading, "Cormorant Garamond", serif)',
              fontSize: '1.3rem',
              fontWeight: 600,
              color: 'var(--text-primary, #1a1a1a)',
              letterSpacing: '0.06em'
            }}>#{orderNumber}</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>

          {/* Buyer info */}
          <div style={{
            background: 'var(--bg-primary, #fff)',
            border: '1px solid var(--border-light, #e8e0d8)',
            padding: '24px'
          }}>
            <h6 style={{
              fontFamily: 'var(--font-body, Inter, sans-serif)',
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--text-muted, #999)',
              marginBottom: '16px',
              paddingBottom: '10px',
              borderBottom: '1px solid var(--border-light, #eee)'
            }}>
              <i className="fas fa-user me-2"></i>Datos del Comprador
            </h6>
            <InfoRow label="Nombre" value={buyerInfo.nombre} />
            <InfoRow label="Email" value={buyerInfo.mail} />
            {buyerInfo.cedula && <InfoRow label="Cédula" value={buyerInfo.cedula} />}
            {buyerInfo.telefono && <InfoRow label="Teléfono" value={buyerInfo.telefono} />}
          </div>

          {/* Shipping + Payment */}
          <div style={{
            background: 'var(--bg-primary, #fff)',
            border: '1px solid var(--border-light, #e8e0d8)',
            padding: '24px'
          }}>
            <h6 style={{
              fontFamily: 'var(--font-body, Inter, sans-serif)',
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--text-muted, #999)',
              marginBottom: '16px',
              paddingBottom: '10px',
              borderBottom: '1px solid var(--border-light, #eee)'
            }}>
              <i className="fas fa-truck me-2"></i>Envío y Pago
            </h6>
            {shippingOption.label && (
              <InfoRow
                label="Método de envío"
                value={shippingOption.label}
                icon={isPickup ? 'fa-store' : 'fa-truck'}
              />
            )}
            {paymentMethodInfo.label && (
              <InfoRow
                label="Método de pago"
                value={paymentMethodInfo.label}
                icon={getPaymentIcon(paymentMethodInfo.method)}
              />
            )}
            {notes && <InfoRow label="Notas" value={notes} />}
          </div>
        </div>

        {/* Items */}
        {items.length > 0 && (
          <div style={{
            background: 'var(--bg-primary, #fff)',
            border: '1px solid var(--border-light, #e8e0d8)',
            padding: '24px',
            marginBottom: '20px'
          }}>
            <h6 style={{
              fontFamily: 'var(--font-body, Inter, sans-serif)',
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--text-muted, #999)',
              marginBottom: '16px',
              paddingBottom: '10px',
              borderBottom: '1px solid var(--border-light, #eee)'
            }}>
              <i className="fas fa-bag-shopping me-2"></i>Productos ({items.length})
            </h6>

            {items.map((item, i) => {
              const imgSrc = item.selectedVariant?.images?.[0] || item.images?.[0] || item.image;
              return (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '12px 0',
                  borderBottom: i < items.length - 1 ? '1px solid var(--border-light, #f0ece8)' : 'none'
                }}>
                  {imgSrc ? (
                    <img
                      src={getDisplayImageUrl(imgSrc)}
                      alt={item.name}
                      style={{ width: 52, height: 52, objectFit: 'cover', flexShrink: 0, border: '1px solid var(--border-light, #eee)' }}
                    />
                  ) : (
                    <div style={{ width: 52, height: 52, background: 'var(--bg-secondary, #f5f3f0)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-shirt" style={{ color: 'var(--text-muted, #ccc)', fontSize: '1.2rem' }}></i>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'var(--font-body, Inter, sans-serif)', fontSize: '0.88rem', fontWeight: 500, color: 'var(--text-primary, #1a1a1a)', marginBottom: 2 }}>
                      {item.name}
                    </div>
                    {(item.selectedVariant?.size || item.selectedVariant?.color) && (
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-muted, #999)' }}>
                        {[item.selectedVariant.size, item.selectedVariant.color].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: 'var(--font-body, Inter, sans-serif)', fontSize: '0.82rem', color: 'var(--text-muted, #999)' }}>×{item.quantity}</div>
                    <div style={{ fontFamily: 'var(--font-body, Inter, sans-serif)', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary, #1a1a1a)' }}>
                      ${(item.price * item.quantity).toLocaleString('es-UY')}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Totals */}
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-light, #e8e0d8)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontFamily: 'var(--font-body, Inter, sans-serif)', fontSize: '0.82rem', color: 'var(--text-muted, #999)' }}>Subtotal</span>
                <span style={{ fontFamily: 'var(--font-body, Inter, sans-serif)', fontSize: '0.82rem', color: 'var(--text-primary, #1a1a1a)' }}>${subtotal.toLocaleString('es-UY')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontFamily: 'var(--font-body, Inter, sans-serif)', fontSize: '0.82rem', color: 'var(--text-muted, #999)' }}>Envío</span>
                <span style={{ fontFamily: 'var(--font-body, Inter, sans-serif)', fontSize: '0.82rem', color: shippingOption.cost === 0 ? 'var(--success, #2ecc71)' : 'var(--text-primary, #1a1a1a)' }}>
                  {shippingOption.cost === 0 ? 'Gratis' : `$${shippingOption.cost.toLocaleString('es-UY')}`}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'var(--font-heading, "Cormorant Garamond", serif)', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary, #1a1a1a)' }}>Total</span>
                <span style={{ fontFamily: 'var(--font-heading, "Cormorant Garamond", serif)', fontSize: '1.15rem', fontWeight: 600, color: 'var(--text-primary, #1a1a1a)' }}>${total.toLocaleString('es-UY')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/tienda')}
            style={{
              fontFamily: 'var(--font-body, Inter, sans-serif)',
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '13px 32px',
              background: 'transparent',
              border: '1px solid var(--border-dark, #1a1a1a)',
              color: 'var(--text-primary, #1a1a1a)',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-store me-2"></i>Seguir comprando
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              fontFamily: 'var(--font-body, Inter, sans-serif)',
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '13px 32px',
              background: 'var(--text-primary, #1a1a1a)',
              border: '1px solid var(--text-primary, #1a1a1a)',
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-home me-2"></i>Ir al inicio
          </button>
        </div>

      </div>
    </div>
    </>
  );
};

const InfoRow = ({ label, value, icon }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 8 }}>
    <span style={{
      fontFamily: 'var(--font-body, Inter, sans-serif)',
      fontSize: '0.75rem',
      color: 'var(--text-muted, #aaa)',
      fontWeight: 500,
      flexShrink: 0
    }}>
      {icon && <i className={`fas ${icon} me-1`}></i>}{label}
    </span>
    <span style={{
      fontFamily: 'var(--font-body, Inter, sans-serif)',
      fontSize: '0.82rem',
      color: 'var(--text-primary, #1a1a1a)',
      fontWeight: 400,
      textAlign: 'right'
    }}>
      {value || '—'}
    </span>
  </div>
);

export default OrderSuccess;